import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import '../styles/templates.css';

interface Template {
  id: string;
  companyId: string | null;
  name: string;
  docType: string;
  category: string;
  language: string;
  body: string;
  variables: Array<{ key: string; label: string }>;
  version: number;
  isActive: boolean;
  isDefault: boolean;
}

const DOC_TYPES = [
  { code: 'contract', label: 'Contratos' },
  { code: 'quote', label: 'Cotizaciones' },
  { code: 'invoice', label: 'Facturas' },
];
const CATEGORIES = [
  { code: 'services', label: 'Servicios' },
  { code: 'products', label: 'Productos' },
  { code: 'general', label: 'General' },
  { code: 'custom', label: 'Personalizada' },
];

type Draft = { id: string | null; name: string; category: string; body: string; isDefault: boolean };

export function Templates() {
  const { company } = useSession();
  const [docType, setDocType] = useState('contract');
  const [list, setList] = useState<Template[] | null>(null);
  const [vars, setVars] = useState<Array<{ key: string; label: string }>>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const load = () => {
    if (!company) return;
    setList(null);
    api.get<Template[]>(`/templates?docType=${docType}`).then(setList).catch(() => setList([]));
  };
  useEffect(load, [company, docType]);
  useEffect(() => {
    api.get<{ variables: Array<{ key: string; label: string }> }>('/templates/variables').then((r) => setVars(r.variables)).catch(() => void 0);
  }, []);

  const openNew = () => { setErr(null); setSavedMsg(null); setDraft({ id: null, name: '', category: 'services', body: '', isDefault: false }); };
  const openEdit = (t: Template) => { setErr(null); setSavedMsg(null); setDraft({ id: t.id, name: t.name, category: t.category, body: t.body, isDefault: t.isDefault }); };

  const insertVar = (key: string) => {
    const ta = bodyRef.current;
    setDraft((d) => {
      if (!d) return d;
      if (!ta) return { ...d, body: `${d.body}{{${key}}}` };
      const start = ta.selectionStart, end = ta.selectionEnd;
      const next = `${d.body.slice(0, start)}{{${key}}}${d.body.slice(end)}`;
      return { ...d, body: next };
    });
  };

  const save = async () => {
    if (!draft) return;
    if (draft.name.trim().length < 2) { setErr('Ponle un nombre a la plantilla.'); return; }
    setBusy(true);
    setErr(null);
    setSavedMsg(null);
    try {
      if (draft.id) {
        const updated = await api.put<Template>(`/templates/${draft.id}`, { name: draft.name.trim(), category: draft.category, body: draft.body, isDefault: draft.isDefault });
        setSavedMsg(`Saved as version ${updated.version}. The previous one is kept.`);
        setDraft({ ...draft, id: updated.id });
      } else {
        const created = await api.post<Template>('/templates', { name: draft.name.trim(), docType, category: draft.category, body: draft.body, isDefault: draft.isDefault });
        setSavedMsg('Plantilla creada.');
        setDraft({ ...draft, id: created.id });
      }
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="main main--wide">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">Administration</span>
            <h1 className="h1">Plantillas de documentos</h1>
            <p className="page-head__sub">Contract, quotation and invoice formats with variables. Editing deletes nothing: a new version is saved and the previous one is archived.</p>
          </div>
          <button className="btn btn--primary" onClick={openNew}><Icon name="plus" size={16} />Nueva plantilla</button>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 18 }}>
        {DOC_TYPES.map((t) => (
          <button key={t.code} className={`tab${docType === t.code ? ' tab--active' : ''}`} onClick={() => { setDocType(t.code); setDraft(null); }}>{t.label}</button>
        ))}
      </div>

      <div className="tpl-layout">
        <div>
          {!list && <div className="faint">Loading…</div>}
          {list && list.length === 0 && <div className="empty" style={{ padding: 20 }}>No hay plantillas de este tipo.</div>}
          {list?.map((t) => (
            <button key={t.id} className={`tpl-item${draft?.id === t.id ? ' tpl-item--active' : ''}`} onClick={() => openEdit(t)}>
              <span className="tpl-item__name">
                {t.name}
                {t.isDefault && <span className="tag tag--accent">por defecto</span>}
              </span>
              <span className="tpl-item__meta">
                <span className="tpl-cat">{CATEGORIES.find((c) => c.code === t.category)?.label ?? t.category}</span>
                <span className="tpl-ver">v{t.version}</span>
                {t.companyId ? <span>· empresa</span> : <span>· shared</span>}
              </span>
            </button>
          ))}
        </div>

        <div>
          {!draft ? (
            <div className="card"><div className="card__body" style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ width: 52, height: 52, margin: '0 auto 14px', borderRadius: 14, display: 'grid', placeItems: 'center', background: 'var(--card-sunken)', color: 'var(--accent)' }}><Icon name="file" size={24} /></div>
              <p className="muted">Elige una plantilla para editarla o crea una nueva.</p>
            </div></div>
          ) : (
            <div className="card tpl-editor">
              <div className="card__body">
                <div className="row gap-12 wrap" style={{ marginBottom: 14 }}>
                  <div className="field grow" style={{ minWidth: 220, marginBottom: 0 }}>
                    <label>Nombre de la plantilla</label>
                    <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="p. ej. Services Agreement" />
                  </div>
                  <div className="field" style={{ width: 180, marginBottom: 0 }}>
                    <label>Category</label>
                    <select className="select" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                      {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <div className="eyebrow" style={{ marginBottom: 8 }}>Variables — pulsa para insertarlas</div>
                  <div className="row gap-8 wrap">
                    {vars.map((v) => <button key={v.key} className="var-chip" onClick={() => insertVar(v.key)} title={v.label}>{`{{${v.key}}}`}</button>)}
                  </div>
                </div>

                <textarea ref={bodyRef} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Write the document body. Use {{cliente}}, {{importe}}, etc." />

                <label className="check-row" style={{ marginTop: 12 }}>
                  <input type="checkbox" checked={draft.isDefault} onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })} />
                  <span>Usar como plantilla por defecto para este tipo</span>
                </label>

                {err && <div className="act-error" style={{ marginTop: 12 }}><Icon name="alert" size={16} />{err}</div>}
                {savedMsg && <div className="ok-note" style={{ marginTop: 12 }}><Icon name="check" size={16} />{savedMsg}</div>}

                <div className="row between center" style={{ marginTop: 16 }}>
                  <span className="faint" style={{ fontSize: 12.5 }}>{draft.id ? 'Saving will create a new version and archive the current one.' : 'It will be created as version 1.'}</span>
                  <button className="btn btn--primary" onClick={save} disabled={busy}>{busy ? 'Guardando…' : draft.id ? 'Save new version' : 'Crear plantilla'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
